import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import {
  AdminService,
  BaseComponent,
  CommunitiesService,
  Community,
  CommunityChannel,
  CommunityFeatures,
  ContentService,
  encodeFilenameForHeader,
  HelperService,
  LanguageService,
  LocalizedString,
  ProductRequirement,
  ProductService,
  resolveLocalizedString,
  UserGroup,
  UserService,
  environment,
  CommunityEnvironment,
} from "@mypxplat/xplat/core";
import { HttpClient } from "@angular/common/http";
import { AppService, WebCommunityService } from "@mypxplat/xplat/web/core";
import { FileUploader } from "ng2-file-upload";
import { debounceTime, Subject, take, takeUntil } from "rxjs";
import { AddEditChannelComponent, AdminUploadFailedComponent, SelectGroupsComponent } from "../../modals";

type RoleKind = "moderator" | "admin" | "required";

type DetailSection = "general" | "channels";

/**
 * Detail / edit view for a single community. Renders inline inside `<myp-communities>` so
 * we don't spin up new routes just for community admin. Segmented bar switches between the
 * General form and the Channels manager.
 *
 * Replaces the earlier modal-based AddEditCommunityComponent and the standalone
 * CommunityManagerComponent — DENG-728 Phase 4.
 */
/** The branding images an admin can upload. */
type BrandingImageSlot = "hero" | "icon";

interface BrandingImageUpload {
  field: "hero_image_url" | "icon_url";
  folder: string;
  label: string;
  hint: string;
  uploader: FileUploader | null;
  display: string | ArrayBuffer | null;
  selectedFile: any;
  progress: number | null;
  hovering: boolean;
  /** Shown beside the preview; precomputed so the template binds a plain property. */
  fileName: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-community-detail",
  templateUrl: "community-detail.component.html",
  standalone: false,
})
export class CommunityDetailComponent extends BaseComponent implements OnInit {
  // [animation] on spinner icons also binds ngbTooltip's boolean input on the same element.
  public readonly spinAnimation: any = "spin";
  /** Existing community in edit mode; leave undefined when creating a new community. */
  @Input() community: Partial<Community> = {};
  @Output() back: EventEmitter<void> = new EventEmitter();
  @Output() communitySaved: EventEmitter<Community> = new EventEmitter();

  public isEdit = false;
  public loading = false;
  /**
   * Scoped loader for the groups lookup — only the three group-driven pickers
   * (moderator roles, admin roles, required groups) block on this. The rest of the
   * form renders immediately with title/description/channels/etc. so the page doesn't
   * feel dead while a slow `GET /admin/groups` call is in flight.
   */
  public groupsLoading = false;
  public hasAccess = true;

  public selectedSection: DetailSection = "general";
  public segmentedOptions = [
    { name: "General", id: "general", selected: true },
    { name: "Channels", id: "channels" },
  ];

  // -- General tab state --
  public groups: UserGroup[] = [];
  public moderatorRoleMap: Record<string, boolean> = {};
  public adminRoleMap: Record<string, boolean> = {};
  /**
   * Pre-computed comma-separated strings for the fake-input template bindings. Kept in
   * sync manually via `recomputeRoleDisplays()` — Angular's default change detection
   * re-evaluates template expressions on every cycle, so a method call in the binding
   * would recompute this string on every keystroke elsewhere in the form. Cached
   * properties avoid that (see the project's "no method calls in template bindings" rule).
   */
  public moderatorRolesDisplay = "";
  public adminRolesDisplay = "";
  public requiredGroupsDisplay = "";
  /**
   * Keyed by `group.name` (matches `moderator_roles` / `admin_roles`) — the community
   * doc's `requiredGroups` array stores names for readability, not ids.
   */
  public requiredGroupMap: Record<string, boolean> = {};
  /**
   * Where the community is served ("Available in"). All three ticked = every environment,
   * saved as null so a community stays everywhere by default.
   */
  public readonly environmentOptions: Array<{ id: CommunityEnvironment; label: string }> = [
    { id: "dev", label: "Dev" },
    { id: "qa", label: "QA" },
    { id: "prod", label: "Prod" },
  ];
  public environmentMap: Record<string, boolean> = { dev: true, qa: true, prod: true };

  // -- Product picker state (for participation_condition === "product_owner") --
  /**
   * Working copy of the community's `requiredProducts` array. Rendered as removable
   * pills below the search box. Written to `community.requiredProducts` at submit time.
   *
   * Load-time coercion: legacy communities that may have been saved with a bare
   * `string[]` (pre-tagged-shape) get read as `{type: "string_id", value: it}` in
   * `ngOnInit`, so we never surface the wrong shape in the picker.
   */
  public requiredProducts: ProductRequirement[] = [];

  /**
   * Which catalog the search fires against. Admin picks explicitly rather than us
   * auto-detecting from the input — real product names ("Tone Master Pro" vs
   * "Studio One Pro") don't distinguish themselves cleanly, and the two catalogs have
   * different underlying schemas ("productNo/sku" vs "string_id").
   */
  public productSearchCatalog: "fender" | "presonus" = "fender";
  /**
   * How the input is interpreted. `"name"` (the default) runs a debounced title search
   * and shows a results dropdown to pick from. The other modes are direct-entry — the
   * admin types the exact identifier and hits Enter (or the Add button), and it lands
   * in `requiredProducts` verbatim as the matching tagged shape. Useful when someone
   * has a spreadsheet of SKUs and wants to paste them in without the search round-trip
   * or when the product name search doesn't turn up the row they know exists.
   *
   * Modes are catalog-scoped:
   *   - Fender  → "name" | "sku" | "productNo"
   *   - PreSonus → "name" | "string_id"
   *
   * Switching catalogs auto-resets the mode if the current one isn't valid in the
   * newly-selected catalog (see `setProductSearchCatalog`).
   */
  public productInputMode: "name" | "sku" | "productNo" | "string_id" = "name";
  /** Free-form input the admin types — meaning depends on `productInputMode`. */
  public productQuery = "";
  /** Search results dropdown — normalized to the tagged shape + a display object. */
  public productResults: Array<{ requirement: ProductRequirement; image?: string; description?: string }> = [];
  public productSearching = false;
  /** Debounced input stream so we don't hammer the search endpoints per keystroke. */
  private productQueryStream$ = new Subject<string>();
  public features: CommunityFeatures = {
    subject_required: false,
    mentions_enabled: true,
    scheduled_posts: false,
    editor: "contenteditable",
    announcements: false,
    view_modes: ["list", "detailed"],
  };
  public branding: {
    hero_image_url: string | null;
    /** Square image for lists (the hub rail, suggestions, explorer); the hero stands in when unset. */
    icon_url: string | null;
    hero_theme: "light" | "dark" | null;
    accent_color: string | null;
    welcome_html: string | null;
    empty_state_html: string | null;
  } = {
    hero_image_url: null,
    icon_url: null,
    hero_theme: null,
    accent_color: null,
    welcome_html: null,
    empty_state_html: null,
  };

  /**
   * Per-locale short description shown under the community title in the layout header.
   * English is the source of truth — non-English rows are populated via `autoTranslateDescription()`.
   */
  public descriptions: LocalizedString = { en: "" };
  public showDescriptionLocales = false;
  public translatingDescription = false;

  // -- Branding image uploaders (hero, icon) --
  /**
   * Same pattern as `<myp-add-edit-product-splash>`: presign against the shared PAE
   * content bucket (`PAE_CONTENT_BUCKET`, publicly readable via `pae-web.presonusmusic.com`
   * in prod) and PUT the file directly. The landing URL is set on the slot's `branding`
   * field on success so a plain admin save picks it up — no separate write path. One
   * uploader per slot; they differ only in the field they fill and the folder they use.
   */
  public brandingImages: Record<BrandingImageSlot, BrandingImageUpload> = {
    hero: {
      field: "hero_image_url",
      folder: "hero-image",
      label: "Hero image",
      hint: "Drop your hero image file here.",
      uploader: null,
      display: null,
      selectedFile: null,
      progress: null,
      hovering: false,
      fileName: "",
    },
    icon: {
      field: "icon_url",
      folder: "icon",
      label: "Icon",
      hint: "Drop a square icon here (shown in lists; the hero image is used when there's none).",
      uploader: null,
      display: null,
      selectedFile: null,
      progress: null,
      hovering: false,
      fileName: "",
    },
  };
  public readonly brandingImageSlots: BrandingImageSlot[] = ["hero", "icon"];

  // -- Channels tab state --
  public channels: CommunityChannel[] = [];
  public channelsLoading = false;
  /**
   * Per-channel in-flight state for the inline "Auto-translate" button on each list row.
   * Keyed by channel `string_id` so a click on one row doesn't grey out the whole list.
   */
  public translatingChannel: Record<string, boolean> = {};

  constructor(
    userService: UserService,
    public appService: AppService,
    public adminService: AdminService,
    public communityService: WebCommunityService,
    public communitiesService: CommunitiesService,
    public contentService: ContentService,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _languageService: LanguageService,
    private _http: HttpClient,
    private _helperService: HelperService,
    private _productService: ProductService
  ) {
    super(userService);
  }

  /**
   * True when the channel has a non-empty translation for every non-English supported
   * language. Bare-string legacy channels count as "not translated" because they only
   * carry English. Empty English title short-circuits to "translated" since there's
   * nothing to nag about.
   */
  isChannelFullyTranslated(channel: CommunityChannel): boolean {
    const title = channel?.title;
    if (!title) return true;
    if (typeof title === "string") return false;
    const en = (title.en || "").trim();
    if (!en) return true;
    const supported = this._languageService.languages.map((l) => l.code).filter((c) => c !== "en");
    return supported.every((code) => (title[code] || "").trim().length > 0);
  }

  /**
   * Inline auto-translate for a single channel: fires one translate request per missing
   * non-English locale in parallel, merges the results into the existing title map, and
   * PUTs the merged payload back to paeapi. Used from the list row so admins don't have
   * to open the edit modal to fill in translations.
   */
  autoTranslateChannel(channel: CommunityChannel, event: Event) {
    event.stopPropagation();
    if (!this.community.string_id) return;
    const title = channel?.title;
    const en = typeof title === "object" ? (title.en || "").trim() : (title || "").trim();
    if (!en) {
      alert(`"${channel.string_id}" has no English title to translate.`);
      return;
    }

    const description = typeof channel.description === "object" ? (channel.description?.en || "").trim() : ((channel.description as string) || "").trim();
    const fields: Record<string, string> = { title: en };
    if (description) fields.description = description;

    const targets = this._languageService.languages.map((l) => l.code).filter((c) => c !== "en");
    if (!targets.length) return;

    this.translatingChannel[channel.string_id] = true;
    const startingTitle: LocalizedString = typeof title === "object" ? { ...title } : { en: en };
    const startingDescription: LocalizedString | null = typeof channel.description === "object" && channel.description ? { ...channel.description } : description ? { en: description } : null;

    const translateUrl = environment.paeApiUrl + `communities/${encodeURIComponent(this.community.string_id)}/channels/${encodeURIComponent(channel.string_id)}/translate`;
    let remaining = targets.length;
    let failed = 0;

    targets.forEach((lang) => {
      this._http.post<Record<string, Record<string, string>>>(translateUrl, JSON.stringify({ fields, targetLang: lang }), this._helperService.getHttpOptions()).subscribe({
        next: (result) => {
          if (result?.title?.[lang]) startingTitle[lang] = result.title[lang];
          if (startingDescription && result?.description?.[lang]) startingDescription[lang] = result.description[lang];
          if (!--remaining) this.saveTranslatedChannel(channel, startingTitle, startingDescription, failed);
        },
        error: () => {
          failed++;
          if (!--remaining) this.saveTranslatedChannel(channel, startingTitle, startingDescription, failed);
        },
      });
    });
  }

  private saveTranslatedChannel(channel: CommunityChannel, title: LocalizedString, description: LocalizedString | null, failedCount: number) {
    if (!this.community.string_id) {
      this.translatingChannel[channel.string_id] = false;
      return;
    }
    // Drop empty locale entries — a blank translation would win over the English fallback
    // when resolveLocalizedString runs on the consumer side.
    const clean = (m: LocalizedString): LocalizedString => {
      const out: LocalizedString = {};
      for (const [k, v] of Object.entries(m)) if ((v || "").trim()) out[k] = v;
      return out;
    };
    const payload: Partial<CommunityChannel> = { title: clean(title) };
    if (description) payload.description = clean(description);

    this.communityService.updateCommunityChannel(this.community.string_id, channel.string_id, payload).subscribe({
      next: (result: any) => {
        this.translatingChannel[channel.string_id] = false;
        // Merge in-place so the row's indicator flips without a full refresh.
        const idx = this.channels.findIndex((c) => c.string_id === channel.string_id);
        if (idx >= 0) this.channels[idx] = { ...this.channels[idx], ...result };
        if (failedCount) alert(`${failedCount} language(s) failed to translate. Try again to fill them in.`);
      },
      error: (err) => {
        console.error("[admin/community-detail] inline autoTranslate save failed", err);
        this.translatingChannel[channel.string_id] = false;
        alert("Failed to save translations. See console for details.");
      },
    });
  }

  ngOnInit() {
    this.isEdit = !!this.community?.string_id;

    // Community-scoped admins (not global admins) can only manage communities where
    // they're in `admin_roles`. Global admins pass this check via the "admin" bypass.
    if (this.isEdit && this.community.string_id) {
      this.hasAccess = this.communitiesService.isCommunityAdmin(this.userService.user, this.community.string_id);
    } else {
      // Create mode is admin-only — leaves current global-admin gating in place.
      this.hasAccess = true;
    }

    // Working copies so form binds don't mutate the parent's community object.
    if (this.community.features) this.features = { ...this.features, ...this.community.features };
    if (this.community.branding) this.branding = { ...this.branding, ...this.community.branding };
    this.brandingImageSlots.forEach((slot) => (this.brandingImages[slot].fileName = this.fileNameOf(this.branding[this.brandingImages[slot].field])));
    // requiredProducts: tagged shape today, but coerce bare strings for any legacy doc
    // that predates the tagged migration (treat unqualified strings as string_id refs).
    if (this.community.requiredProducts?.length) {
      this.requiredProducts = this.community.requiredProducts.map((entry: any) => {
        if (typeof entry === "string") return { type: "string_id", value: entry };
        return entry as ProductRequirement;
      });
    }

    // Debounced product-search stream — one endpoint fires per pause in typing, not
    // per keystroke. 300ms matches other typeaheads in the app (add-edit-product-splash).
    this.productQueryStream$.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe((q) => this.runProductSearch(q));
    if (!this.community.participation_condition) this.community.participation_condition = "none";

    // Description: accept the current map shape or a legacy bare string. Always keep an `en`
    // key so ngModel has something to bind to.
    const existingDescription = this.community.description;
    if (typeof existingDescription === "object" && existingDescription) {
      this.descriptions = { en: "", ...existingDescription };
    } else if (typeof existingDescription === "string" && existingDescription) {
      this.descriptions = { en: existingDescription };
    }

    (this.community.moderator_roles ?? []).forEach((name) => (this.moderatorRoleMap[name] = true));
    (this.community.admin_roles ?? []).forEach((name) => (this.adminRoleMap[name] = true));
    (this.community.requiredGroups ?? []).forEach((name) => (this.requiredGroupMap[name] = true));
    const envs = this.community.environments;
    if (Array.isArray(envs) && envs.length) this.environmentMap = { dev: envs.includes("dev"), qa: envs.includes("qa"), prod: envs.includes("prod") };
    this.recomputeRoleDisplays();

    // Fire-and-forget — groups only feed the moderator/admin/required-group pickers,
    // which have their own scoped loader. The rest of the form (title, description,
    // channels, branding, hero image, product picker, etc.) doesn't touch this list
    // and shouldn't wait on it. CommunitiesComponent primes the AdminService cache
    // when the section mounts, so this is usually synchronous anyway; the async
    // guard is here for cold-cache route hits.
    this.groupsLoading = true;
    this.adminService.getGroups().subscribe({
      next: (result: any) => {
        this.groups = (result || []).slice().sort((a: UserGroup, b: UserGroup) => (a.name > b.name ? 1 : -1));
        this.groupsLoading = false;
      },
      error: (err) => {
        console.error("[admin/community-detail] getGroups failed", err);
        this.groupsLoading = false;
      },
    });

    // Restore the section from `?section=` so a refresh (or a link) lands on the right tab.
    const sectionParam = this._activatedRoute.snapshot.queryParams["section"];
    if (sectionParam === "channels" && this.isEdit) this.selectedSection = "channels";

    if (this.isEdit) this.refreshChannels();

    this.brandingImageSlots.forEach((slot) => this.setupBrandingImageUploader(slot));
  }

  // -- Branding image upload (hero, icon) --

  private setupBrandingImageUploader(slot: BrandingImageSlot) {
    const state = this.brandingImages[slot];
    state.fileName = this.fileNameOf(this.branding[state.field]);
    state.uploader = new FileUploader({
      isHTML5: true,
      method: "PUT",
      queueLimit: 1,
      autoUpload: false,
      disableMultipart: true,
      url: "",
    });
    state.uploader.onAfterAddingFile = (file) => {
      file.withCredentials = false;
      if (!file.file.type.startsWith("image/")) {
        alert("Please select an image file");
        state.uploader.clearQueue();
        return;
      }
      state.selectedFile = file;
      state.fileName = file.file.name;
      this.contentService.uploadSession.filesMap[file.file.name] = { file };
      const reader = new FileReader();
      reader.onload = () => (state.display = reader.result);
      reader.onerror = (err) => console.error(`[${state.folder}] read error`, err);
      reader.readAsDataURL(file._file);
      this.uploadBrandingImage(slot);
    };
    state.uploader.onProgressAll = (progress: any) => {
      const key = state.selectedFile?.file?.name;
      if (key && this.contentService.uploadSession.filesMap[key]) {
        this.contentService.uploadSession.filesMap[key].progress = progress;
      }
      state.progress = progress < 100 ? progress : null;
    };
    state.uploader.onErrorItem = (error: any) => {
      this.appService.showModal(AdminUploadFailedComponent, { size: "lg" }).componentInstance.data = this.contentService.uploadSession.filesMap[error.file.name];
      delete this.contentService.uploadSession.filesMap[error.file.name];
    };
  }

  public fileOverBrandingImage(slot: BrandingImageSlot, over: boolean) {
    this.brandingImages[slot].hovering = over;
  }

  public removeBrandingImage(slot: BrandingImageSlot) {
    const state = this.brandingImages[slot];
    state.display = null;
    state.selectedFile = null;
    state.fileName = "";
    state.uploader?.clearQueue();
    this.branding[state.field] = null;
  }

  private uploadBrandingImage(slot: BrandingImageSlot) {
    const state = this.brandingImages[slot];
    if (!state.selectedFile) return;
    // Bucket the file under the community's string_id so uploads made before Save (i.e. in
    // create mode) still land in a deterministic path. New communities without a string_id
    // yet get a "_new" holding bay — an admin is expected to type string_id first anyway.
    const communityScope = this.community.string_id || "_new";
    const folder = `community-branding/${communityScope}/${state.folder}`;
    state.progress = 1;

    this.contentService
      .getPresignedUploadURL({
        folder,
        fileName: state.selectedFile.file.name,
        type: "generic_file",
      })
      .subscribe({
        next: (result: any) => {
          if (!result?.file || !result?.url) {
            state.progress = null;
            this.removeBrandingImage(slot);
            alert("There was an error uploading this file, please try again.");
            return;
          }
          this.branding[state.field] = result.file.url;
          this.contentService.uploadSession.filesMap[result.file.fileName].paeEntity = result.file;
          state.uploader.queue.forEach((file) => {
            file.headers = [
              { name: "Content-Type", value: undefined as any },
              { name: "Content-Disposition", value: encodeFilenameForHeader(state.selectedFile.file.name) },
            ];
            file.url = result.url;
            state.uploader.uploadItem(file);
          });
        },
        error: (err) => {
          console.error(`[${state.folder}] presign failed`, err);
          state.progress = null;
          this.removeBrandingImage(slot);
          alert("There was an error preparing the upload. Please try again.");
        },
      });
  }

  /** The last path segment of an uploaded file's URL, for the "current file" line. */
  private fileNameOf(url: string | null | undefined): string {
    const parts = String(url || "").split("/");
    return parts[parts.length - 1] || "";
  }

  switchSection(id: DetailSection) {
    if (id !== "general" && id !== "channels") return;
    this.selectedSection = id;
    // Mirror URL so refresh / share links respect the tab. `replaceUrl: true` keeps the
    // browser history clean — tab switches shouldn't stack. Matches the AdminLearngroupDetail
    // pattern.
    this._router.navigate([], { relativeTo: this._activatedRoute, replaceUrl: true, queryParams: { section: id }, queryParamsHandling: "merge" });
    if (id === "channels" && !this.channels.length && !this.channelsLoading) {
      this.refreshChannels();
    }
  }

  /**
   * Return the currently selected names for a role kind. Read by the template to render
   * the fake-input contents.
   */
  selectedNamesFor(kind: RoleKind): string[] {
    const map = kind === "moderator" ? this.moderatorRoleMap : kind === "admin" ? this.adminRoleMap : this.requiredGroupMap;
    return Object.keys(map).filter((k) => map[k]);
  }

  /** Refresh the three display strings after any role-map mutation. */
  private recomputeRoleDisplays() {
    this.moderatorRolesDisplay = this.selectedNamesFor("moderator").join(", ");
    this.adminRolesDisplay = this.selectedNamesFor("admin").join(", ");
    this.requiredGroupsDisplay = this.selectedNamesFor("required").join(", ");
  }

  /**
   * Open the shared SelectGroupsComponent modal seeded with the current selection for
   * this role kind. On Save, replace the local map so ngModel-driven inputs update.
   */
  openGroupPicker(kind: RoleKind) {
    // Groups not loaded yet — the input's visually a skeleton, but a keyboard shortcut
    // could still fire this. No-op rather than open a modal with an empty list.
    if (this.groupsLoading) return;
    const titles: Record<RoleKind, string> = {
      moderator: "Select Moderator Roles",
      admin: "Select Admin Roles",
      required: "Select Required Groups",
    };
    const descriptions: Record<RoleKind, string> = {
      moderator: "Users in the selected groups can moderate this community (delete/flag posts, ban users).",
      admin: "Users in the selected groups can edit this community's config and manage its channels.",
      required: "Users must be in at least one of these groups to see this community. Only used when Visibility is set to Members only.",
    };
    const modalRef = this.appService.showModal(SelectGroupsComponent, { size: "lg" });
    modalRef.componentInstance.title = titles[kind];
    modalRef.componentInstance.description = descriptions[kind];
    modalRef.componentInstance.groups = this.groups;
    modalRef.componentInstance.selectedNames = this.selectedNamesFor(kind);
    modalRef.componentInstance.groupsSelected.pipe(take(1), takeUntil(this.destroy$)).subscribe((names: string[]) => {
      const target = kind === "moderator" ? this.moderatorRoleMap : kind === "admin" ? this.adminRoleMap : this.requiredGroupMap;
      // Wipe and rewrite rather than merge — the modal's list is authoritative on Save.
      for (const k of Object.keys(target)) delete target[k];
      names.forEach((n) => (target[n] = true));
      this.recomputeRoleDisplays();
    });
  }
  /** Non-English supported languages for the per-locale description editor. */
  get descriptionOtherLocales(): string[] {
    return this._languageService.languages.map((l) => l.code).filter((c) => c !== "en");
  }

  /**
   * Fill every non-English description slot from the English source via the generic
   * `POST /admin/translate` endpoint. Leaves the English field alone; empty English is
   * treated as "nothing to translate".
   */
  autoTranslateDescription() {
    const en = (this.descriptions.en || "").trim();
    if (!en) {
      alert("Enter an English description first, then click Auto-translate.");
      return;
    }
    const targets = this.descriptionOtherLocales;
    if (!targets.length) return;

    this.translatingDescription = true;
    let remaining = targets.length;
    let failed = 0;

    targets.forEach((lang) => {
      this.adminService.translate({ fields: { description: en }, targetLang: lang }).subscribe({
        next: (result) => {
          const translated = result?.description?.[lang];
          if (translated) this.descriptions[lang] = translated;
          if (!--remaining) this.finishAutoTranslateDescription(failed);
        },
        error: () => {
          failed++;
          if (!--remaining) this.finishAutoTranslateDescription(failed);
        },
      });
    });
  }

  private finishAutoTranslateDescription(failedCount: number) {
    this.translatingDescription = false;
    this.showDescriptionLocales = true;
    if (failedCount) alert(`${failedCount} language(s) failed to translate. Try again to fill them in.`);
  }

  toggleViewMode(mode: "list" | "detailed") {
    const idx = this.features.view_modes.indexOf(mode);
    if (idx >= 0) this.features.view_modes.splice(idx, 1);
    else this.features.view_modes.push(mode);
  }

  // -- Product picker --------------------------------------------------------------
  //
  // Admin picks the catalog (Fender FMIC vs PreSonus Product table), then types a
  // product name. Fires the corresponding title search. Results are normalized to the
  // tagged `ProductRequirement` shape at pick time so downstream code (frontend gate,
  // Firestore rule) doesn't care which catalog the entry came from.

  setProductSearchCatalog(catalog: "fender" | "presonus") {
    if (this.productSearchCatalog === catalog) return;
    this.productSearchCatalog = catalog;
    // Catalog only feeds "name" search — direct-entry modes carry their catalog
    // implicitly through the identifier type (SKU/productNo = Fender, string_id =
    // PreSonus), so we don't need to touch `productInputMode` here.
    this.productResults = [];
    this.productSearching = false;
    if (this.productInputMode === "name" && this.productQuery.trim()) {
      this.productSearching = true;
      this.productQueryStream$.next(this.productQuery.trim());
    }
  }

  setProductInputMode(mode: "name" | "sku" | "productNo" | "string_id") {
    if (this.productInputMode === mode) return;
    this.productInputMode = mode;
    // Every mode uses the same picker flow — clear the results panel on mode change
    // and re-fire the search against the new field if there's something to search.
    this.productResults = [];
    this.productSearching = false;
    if (this.productQuery.trim()) {
      this.productSearching = true;
      this.productQueryStream$.next(this.productQuery.trim());
    }
  }

  onProductQueryInput(q: string) {
    this.productQuery = q;
    if (!q.trim()) {
      this.productResults = [];
      this.productSearching = false;
      return;
    }
    this.productSearching = true;
    this.productQueryStream$.next(q.trim());
  }

  private runProductSearch(q: string) {
    if (!q) {
      this.productResults = [];
      this.productSearching = false;
      return;
    }
    // Every mode routes through the same "search → picker → confirm" flow, so we
    // never save a raw identifier that doesn't correspond to an actual product row.
    // The mode picks which endpoint + search field to hit; the response is normalized
    // to the same `ProductRequirement`-shaped result rows so the template renders one
    // dropdown pattern regardless of catalog / mode.
    switch (this.productInputMode) {
      case "name":
        if (this.productSearchCatalog === "fender") this.searchFender({ title: q });
        else this.searchPresonus("title", q);
        break;
      case "sku":
        this.searchFender({ sku: q });
        break;
      case "productNo":
        this.searchFender({ productNo: q });
        break;
      case "string_id":
        // NB: paeapi interpolates `searchField` directly into `Product.${searchField}`,
        // and the column is camelCase `stringId` in the DB — passing "string_id" here
        // 500s with "Unknown column 'Product.string_id'". Our own `ProductRequirement.type`
        // stays snake_case ("string_id") because that's the storage/config convention.
        this.searchPresonus("stringId", q);
        break;
    }
  }

  private searchFender(params: { title?: string; sku?: string; productNo?: string }) {
    this._productService.searchFenderGear({ ...params, pageSize: 8 }).subscribe({
      next: (result: any) => {
        const items: any[] = result?.items || result?.results || [];
        this.productResults = items
          .map((item) => {
            // Prefer productNo (identifies a product family) over SKU (variant-specific).
            // Fall back to SKU only when productNo isn't present on the result — some FMIC
            // rows are one-off SKUs without a productNo grouping.
            const productNo = item.productNo ? String(item.productNo) : "";
            const sku = item.sku ? String(item.sku) : "";
            if (!productNo && !sku) return null;
            const image = this.extractImage(item);
            const label = item.name || item.title || productNo || sku;
            const requirement: ProductRequirement = productNo ? { type: "productNo", value: productNo, label, image } : { type: "sku", value: sku, label, image };
            return this.toProductResult(requirement, item);
          })
          .filter((r): r is NonNullable<typeof r> => r !== null);
        this.productSearching = false;
      },
      error: (err) => {
        console.error("[admin/community-detail] FMIC search failed", err);
        this.productResults = [];
        this.productSearching = false;
      },
    });
  }

  private searchPresonus(searchField: "title" | "stringId", q: string) {
    this._productService.searchv2(searchField, q, 8).subscribe({
      next: (result: any) => {
        const items: any[] = Array.isArray(result) ? result : result?.results || [];
        this.productResults = items
          .map((item) => {
            const stringId = item.stringId || item.string_id;
            if (!stringId) return null;
            const requirement: ProductRequirement = {
              type: "string_id",
              value: stringId,
              label: item.title || stringId,
              image: this.extractImage(item),
            };
            return this.toProductResult(requirement, item);
          })
          .filter((r): r is NonNullable<typeof r> => r !== null);
        this.productSearching = false;
      },
      error: (err) => {
        console.error("[admin/community-detail] PreSonus product search failed", err);
        this.productResults = [];
        this.productSearching = false;
      },
    });
  }

  /**
   * FMIC (`image` / `imageUrl`) and the PreSonus Product table (`thumbnail`) use
   * different field names for the display image. Normalize here so downstream code
   * (result-row rendering + `requirement.image` persistence) just reads one value.
   * Empty string is coerced to `undefined` so the pill/prompt can fall back cleanly.
   */
  private extractImage(item: any): string | undefined {
    const image = item?.image || item?.imageUrl || item?.thumbnail || item?.image_url;
    return image && typeof image === "string" ? image : undefined;
  }

  private toProductResult(requirement: ProductRequirement, item: any) {
    return {
      requirement,
      image: requirement.image,
      description: item?.description || item?.DTYPE,
    };
  }

  addRequiredProduct(requirement: ProductRequirement) {
    // Dedupe by (type, value) — a re-add of an already-selected product is a no-op
    // rather than a duplicate pill.
    const already = this.requiredProducts.some((r) => r.type === requirement.type && r.value === requirement.value);
    if (!already) this.requiredProducts.push(requirement);
    this.productQuery = "";
    this.productResults = [];
  }

  removeRequiredProduct(requirement: ProductRequirement) {
    this.requiredProducts = this.requiredProducts.filter((r) => !(r.type === requirement.type && r.value === requirement.value));
  }

  submit() {
    if (!this.community.string_id) {
      alert("string_id is required.");
      return;
    }
    if (!this.community.title) {
      alert("title is required.");
      return;
    }
    const environments = this.environmentOptions.map((o) => o.id).filter((id) => this.environmentMap[id]);
    if (!environments.length) {
      // paeapi reads an empty list as "every environment" — the opposite of unticking all.
      alert("Choose at least one environment. To keep a community out of the list everywhere, use Hidden.");
      return;
    }

    const moderator_roles = Object.keys(this.moderatorRoleMap).filter((k) => this.moderatorRoleMap[k]);
    const admin_roles = Object.keys(this.adminRoleMap).filter((k) => this.adminRoleMap[k]);

    // requiredGroups / requiredProducts only carry meaning under specific participation
    // conditions; null out otherwise so stale selections don't linger when an admin
    // switches conditions.
    const requiredGroups = this.community.participation_condition === "group_member" ? Object.keys(this.requiredGroupMap).filter((k) => this.requiredGroupMap[k]) : null;
    const requiredProducts = this.community.participation_condition === "product_owner" ? this.requiredProducts : null;

    // Drop empty locale entries — a blank translation would win over the English fallback
    // when `resolveLocalizedString` runs on the consumer side. `null` (rather than an
    // empty map) lets paeapi treat an all-blank description as "no description set".
    const cleanDescription: LocalizedString = {};
    for (const [k, v] of Object.entries(this.descriptions)) if ((v || "").trim()) cleanDescription[k] = v.trim();
    const description = Object.keys(cleanDescription).length ? cleanDescription : null;

    const payload: any = {
      string_id: this.community.string_id,
      title: this.community.title,
      description,
      participation_condition: this.community.participation_condition,
      order: this.community.order ?? 0,
      hidden: !!this.community.hidden,
      employee_only: !!this.community.employee_only,
      environments: environments.length === this.environmentOptions.length ? null : environments,
      moderator_roles,
      admin_roles,
      features: this.features,
      branding: this.branding,
      requiredGroups,
      requiredProducts,
    };

    this.loading = true;
    const request$ = this.isEdit ? this.adminService.updateCommunity(this.community.string_id, payload) : this.adminService.createCommunity(payload);
    request$.subscribe({
      next: (result: any) => {
        this.loading = false;
        this.communitySaved.next(result);
        // Invalidate the CommunitiesService cache after every save so a back-nav to the
        // list view (or another consumer reading getCommunity(id)) sees fresh data.
        this.communitiesService.refresh();
        // After a create, promote into edit mode so subsequent saves PUT instead of POST,
        // and the Channels tab (disabled in create mode) becomes reachable.
        if (!this.isEdit) {
          this.community = { ...this.community, ...result };
          this.isEdit = true;
          this.refreshChannels();
        }
      },
      error: (err) => {
        console.error("[admin/community-detail] save failed", err);
        this.loading = false;
        const msg = err?.error?.error || err?.message || "Failed to save community. See console for details.";
        alert(msg);
      },
    });
  }

  // -- Channels tab --

  refreshChannels() {
    if (!this.community.string_id) return;
    this.channelsLoading = true;
    // `force` — a management view must never render a session snapshot of what it edits.
    this.communityService.getCommunityChannels(this.community.string_id, true).subscribe({
      next: (result) => {
        this.channelsLoading = false;
        // Sort by explicit `ordering`, then English title. Title is a per-locale map now,
        // so pluck `.en` (or the bare-string form for legacy channels) before comparing.
        const enTitle = (c: any) => (typeof c?.title === "object" ? c.title?.en || "" : c?.title || "");
        this.channels = (result || []).slice().sort((a, b) => {
          const ao = a.ordering ?? 0;
          const bo = b.ordering ?? 0;
          if (ao !== bo) return ao - bo;
          return enTitle(a).localeCompare(enTitle(b));
        });
      },
      error: (err) => {
        console.error("[admin/community-detail] getCommunityChannels failed", err);
        this.channelsLoading = false;
      },
    });
  }

  addEditChannel(channel?: CommunityChannel) {
    if (!this.community.string_id) return;
    const modalRef = this.appService.showModal(AddEditChannelComponent, { size: "lg" });
    modalRef.componentInstance.communityStringId = this.community.string_id;
    modalRef.componentInstance.groups = this.groups;
    if (channel) modalRef.componentInstance.channel = { ...channel };
    modalRef.componentInstance.channelSaved.pipe(take(1), takeUntil(this.destroy$)).subscribe(() => {
      this.refreshChannels();
    });
  }

  deleteChannel(channel: CommunityChannel, event: Event) {
    event.stopPropagation();
    if (!this.community.string_id) return;
    // Titles are per-locale maps now — resolve the English form for the prompt so admins
    // don't see `[object Object]`. Falls back to string_id if the title's blank.
    const label = resolveLocalizedString(channel.title, this._languageService.selectedLanguage?.code || "en", channel.string_id);
    this.appService
      .confirm(`Archive the "${label}" channel?\n\nHistorical posts referencing this channel keep their tag, but the channel stops appearing in listings.`, "Archive")
      .then(() => {
        this.communityService.deleteCommunityChannel(this.community.string_id!, channel.string_id).subscribe({
          next: () => this.refreshChannels(),
          error: (err) => {
            console.error("[admin/community-detail] delete channel failed", err);
            this.appService.alert("Failed to archive channel. See console for details.");
          },
        });
      })
      .catch(() => {
        /* user canceled — no-op */
      });
  }

  goBack() {
    this.back.emit();
  }
}
